/**
 * What the agents have done on the campaign you're looking at.
 *
 * Campaigns live in Beatly and agent work lives in cowork; until now nothing
 * joined them, so a campaign page could not show that Saga had already produced
 * a status doc for it. This is that join, surfaced where the campaign is.
 */
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, FileStack, MessagesSquare, ChevronRight } from 'lucide-react';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';

function go(path: string) {
  window.dispatchEvent(new CustomEvent('agent-rail-navigate', { detail: path }));
}

export function CampaignWorkCard({ campaignId }: { campaignId: string }) {
  const { data } = useQuery({
    queryKey: ['cowork', 'campaign-work', campaignId],
    queryFn: () => coworkApi.campaignWork.get(campaignId),
    staleTime: 30_000,
  });

  const s = data?.summary;
  const total = (s?.openTasks ?? 0) + (s?.doneTasks ?? 0) + (s?.deliverables ?? 0);
  // Nothing yet is the normal state on a fresh campaign — stay out of the way.
  if (!s || total === 0) return null;

  const rows = [
    {
      icon: ClipboardList,
      label: s.openTasks > 0 ? `${s.openTasks} open` : `${s.doneTasks} done`,
      sub: 'tasks',
      onClick: () => go('/agents/tasks'),
    },
    {
      icon: FileStack,
      label: String(s.deliverables),
      sub: s.deliverables === 1 ? 'deliverable' : 'deliverables',
      onClick: () => go(`/agents/deliverables?campaign=${encodeURIComponent(campaignId)}`),
    },
    {
      icon: MessagesSquare,
      label: String(s.conversations),
      sub: s.conversations === 1 ? 'thread' : 'threads',
      onClick: () => go('/agents/chats'),
    },
  ];

  return (
    <div className="border-b border-gray-100 px-4 py-3">
      <p className="mb-2 text-[10px] uppercase tracking-wider text-[#98a2b3]" style={{ fontWeight: 600 }}>
        Agent work on this campaign
      </p>
      <div className="grid grid-cols-3 gap-1.5">
        {rows.map((r) => {
          const Icon = r.icon;
          return (
            <button
              key={r.sub}
              type="button"
              onClick={r.onClick}
              className="flex flex-col items-start gap-0.5 rounded-xl border border-gray-100 px-2.5 py-2 text-left transition-colors hover:border-[#be43ff]/30 hover:bg-[#be43ff]/[0.03]"
            >
              <Icon className="h-3.5 w-3.5 text-[#98a2b3]" aria-hidden />
              <span className="text-[13px] text-[#101828] tabular-nums" style={{ fontWeight: 600 }}>
                {r.label}
              </span>
              <span className="text-[10px] text-[#98a2b3]">{r.sub}</span>
            </button>
          );
        })}
      </div>

      {data?.deliverables?.[0] && (
        <button
          type="button"
          onClick={() => go(`/agents/deliverables?campaign=${encodeURIComponent(campaignId)}`)}
          className="mt-2 flex w-full items-center gap-1.5 rounded-lg px-1 py-1 text-left text-[11px] text-[#667085] transition-colors hover:text-[#be43ff]"
        >
          <span className="min-w-0 flex-1 truncate">Latest: {data.deliverables[0].title}</span>
          <ChevronRight className="h-3 w-3 shrink-0" aria-hidden />
        </button>
      )}
    </div>
  );
}
